import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { formatCurrency } from "../../../../utils/utils";
import { allocateCacheShare, fetchTeamRanges } from "../services/cacheShareServices";
import {
  MAX_LOCK,
  allocateRequestOf,
  amountText,
  applyAllocation,
  budgetPurposeOf,
  importPayload,
  locksOf,
  mergeAllocation,
  mergeRows,
  mergeScored,
  padAmount,
  parseMoney,
  poolSummary,
  rowId,
  toImportRow,
  toRow,
} from "../allocationModel";

const NO_BUDGETS = { rs: 0, nil: 0 };
// No split: the roster is not scored yet, or neither pool has a budget.
// `budgets` are the ones an answer was split at.
const NO_ALLOCATION = { rs: null, nil: null, budgets: NO_BUDGETS, status: "idle", error: null };

// The allocate endpoint is posted this long after the last change, so a
// burst of changes (a lock, then a hide) makes one call.
export const ALLOCATE_DELAY_MS = 120;

// A failed calculator call: the HTTP status (0 when nothing answered), the
// server's own message, read from the services' "HTTP 503: {...}" errors or
// from an axios error, and the server's error code when it sent one.
export const failureOf = (err) => {
  const text = String(err?.message || "");
  const match = text.match(/^HTTP (\d{3}): ([\s\S]*)$/);
  const status = match ? Number(match[1]) : Number(err?.response?.status) || 0;
  let body = match ? match[2] : err?.response?.data;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch (e) {
      // a plain-text body is the message itself
    }
  }
  const detail = String((typeof body === "string" ? body : body?.message || body?.error || body?.detail) || "").trim();
  const code = body && typeof body === "object" && typeof body.code === "string" ? body.code : undefined;
  // An HTML error page (a gateway's) is no message.
  return { status, detail: detail.startsWith("<") || detail.length > 300 ? "" : detail, code };
};

// The data provider gave nothing and the server has nothing saved for this
// team (503 provider_unavailable): asking again at once would get the same.
export const PROVIDER_UNAVAILABLE = "provider_unavailable";
const isProviderUnavailable = (failure) => failure?.code === PROVIDER_UNAVAILABLE;

// Nothing answered, or a gateway, timeout or rate limit: worth one more try.
const TRANSIENT = new Set([0, 408, 429, 502, 503, 504]);
export const isTransient = (status) => TRANSIENT.has(status);

// Only a call that failed quickly (a dropped connection, Cerebro's own
// error) is tried again at once. One that ran into the gateway's 30 second
// limit would most likely do so again, so the coach sees Try again at once
// instead of waiting as long a second time.
export const RETRY_WITHIN_MS = 10000;

// The words the page shows for a failure. A refusal says why (the server's
// message, such as the CacheShare plan gate); a data provider that is paused
// with nothing saved for the team says so in the server's words; a roster
// service that did not answer asks for another try.
export const failureText = ({ status, detail, code }) => {
  if (code === PROVIDER_UNAVAILABLE) return detail || "Live stats are paused and none are saved for this team yet. Try again later.";
  if (status === 401) return "Your session has ended. Log in again to load this team.";
  if (status === 403) return detail || "This account cannot use CacheShare.";
  if (status >= 400 && status < 500 && !isTransient(status)) return detail ? `This team could not be scored: ${detail}` : "This team could not be scored.";
  return "The roster service did not answer. Try again in a moment.";
};

const toggleIn = (set, id) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

// `locks` with `id` locked at `amount`, or unlocked for null; the same map
// when nothing changes, so an unchanged lock posts nothing.
const withLock = (locks, id, amount) => {
  if (amount === null) {
    if (!(id in locks)) return locks;
    const { [id]: dropped, ...rest } = locks;
    return rest;
  }
  return locks[id] === amount ? locks : { ...locks, [id]: amount };
};

// Coach Mode V2 CacheShare™ state for one team: the scored roster, imported
// players, the revenue share and NIL pools, hidden players and each pool's
// locked amounts. `open(key, slice)` loads a team and restores its saved
// slice; `slice` is the state to save back under `openedKey`.
//
// The calculator runs when a team opens (at the budget last distributed),
// on Distribute with a revenue share budget and after imports or roster
// changes, with the revenue share locks and hidden players. The split the
// tables show (`allocation`: both pools with their locks) comes from the
// allocate endpoint, posted ALLOCATE_DELAY_MS after the scored rows, the
// distributed budgets, the hidden players or the locks last changed. A
// newer post aborts the one before and only the newest answer is kept. A
// post that fails keeps the last answer on screen, sets `allocation.status`
// to "error", and `retryAllocate()` posts again.
//
// A calculator call that fails quickly because nothing answered (or a
// gateway refused it) is tried once more at once (RETRY_WITHIN_MS), unless
// the server said the data provider is paused with nothing saved for the
// team (provider_unavailable). A call that still fails sets `error`
// ({action: "load" | "distribute", text}) and `retry()` runs it again, so
// the page shows what went wrong with a way to try again, never a silent
// empty roster. An answer for a team that is no longer open is dropped.
//
// `dataStatus` is the last answer's: live, or "stale" while the provider is
// paused and the server scores its saved copy (the page shows a notice).
export const useAllocationBoard = ({ team, sport, scope }) => {
  const [roster, setRoster] = useState([]);
  const [added, setAdded] = useState([]);
  // The open team's roster has come back from the calculator at least once.
  const [scored, setScored] = useState(false);
  const [hiddenRs, setHiddenRs] = useState(() => new Set());
  const [hiddenNil, setHiddenNil] = useState(() => new Set());
  // Locked amounts by player id: revenue share (saved as payoutOverrides)
  // and NIL (saved as nilLocks).
  const [rsLocks, setRsLocks] = useState({});
  const [nilLocks, setNilLocks] = useState({});
  const [amounts, setAmounts] = useState({ rs: "0.00", nil: "0.00" });
  const [distributed, setDistributed] = useState(NO_BUDGETS);
  const [allocation, setAllocation] = useState(NO_ALLOCATION);
  const [allocateRun, setAllocateRun] = useState(0);
  const [loading, setLoading] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [openedKey, setOpenedKey] = useState("");
  const [error, setError] = useState(null);
  const [dataStatus, setDataStatus] = useState(null);

  // Calls read the team of the current render; a newer call wins.
  const target = useRef({ team, sport, scope });
  target.current = { team, sport, scope };
  const ticket = useRef(0);
  const allocateTicket = useRef(0);

  // `locks` and `hidden` are the revenue share locks and hidden player ids
  // the calculator answers payoutAllocated with.
  const rescore = useCallback(async (budget, imports, { action = "load", locks = {}, hidden = [] } = {}) => {
    const { team: t, sport: s, scope: sc } = target.current;
    if (!t) return null;
    const mine = ++ticket.current;
    const current = () => mine === ticket.current;
    setLoading(true);
    setError(null);
    const request = () =>
      fetchTeamRanges(t, {
        sport: s,
        scope: sc,
        revshareBudget: budget,
        importedAthleteIds: imports.length ? imports.map(rowId) : undefined,
        importedAthletes: imports.length ? imports.map(importPayload) : undefined,
        lockedAllocations: locks,
        hiddenIds: hidden,
      });
    try {
      let data;
      const started = Date.now();
      try {
        data = await request();
      } catch (first) {
        const failure = failureOf(first);
        if (!current() || isProviderUnavailable(failure) || !isTransient(failure.status) || Date.now() - started > RETRY_WITHIN_MS) throw first;
        data = await request();
      }
      if (!current()) return null;
      setDataStatus(data?.dataStatus || null);
      const rows = (data?.results || []).map(toRow).filter((r) => r.sportsRadarId);
      const importIds = new Set(imports.map(rowId));
      const byId = new Map(rows.map((r) => [r.sportsRadarId, r]));
      const next = { roster: rows.filter((r) => !importIds.has(r.sportsRadarId)), added: imports.map((p) => mergeScored(p, byId.get(rowId(p)))) };
      setRoster(next.roster);
      setAdded(next.added);
      setScored(true);
      return next;
    } catch (err) {
      if (current()) {
        const text = failureText(failureOf(err));
        setError({ action, text });
        toast.error(text, { id: "cacheshare-ranges", duration: 6000 });
      }
      return null;
    } finally {
      if (current()) setLoading(false);
    }
  }, []);

  // Score the open team at the budget last distributed, with its revenue
  // share locks and hidden players.
  const load = useCallback(
    (budgets, imports, { hiddenRs: hiddenRsIds, rsLocks: locks }) => rescore(budgets.rs, imports, { locks, hidden: [...hiddenRsIds] }),
    [rescore],
  );

  const open = useCallback(
    (key, slice = {}) => {
      const imports = (slice.addedPlayers || []).filter((p) => p && typeof p === "object").map((p) => toImportRow(p));
      const hiddenNilIds = new Set(slice.hiddenNil || []);
      const hiddenRsIds = new Set(slice.hiddenRs || []);
      // Manual allocations saved before locks existed read as revenue share
      // locks.
      const savedRsLocks = locksOf(slice.payoutOverrides);
      // The pools run at the budgets last distributed; the inputs show what
      // was typed, which may be newer.
      const budgets = { rs: parseMoney(slice.distributedRs), nil: parseMoney(slice.distributedNil) };
      ticket.current += 1; // drop answers for the previous team
      setOpenedKey(key || "");
      setRoster([]);
      setAdded(imports);
      setScored(false);
      setHiddenRs(hiddenRsIds);
      setHiddenNil(hiddenNilIds);
      setRsLocks(savedRsLocks);
      setNilLocks(locksOf(slice.nilLocks));
      setAmounts({ rs: padAmount(slice.revenueAmount), nil: padAmount(slice.nilAmount) });
      setDistributed(budgets);
      setAllocation(NO_ALLOCATION);
      setLoading(false);
      setError(null);
      setDataStatus(null);
      if (!key) return;
      load(budgets, imports, { hiddenRs: hiddenRsIds, rsLocks: savedRsLocks });
    },
    [load],
  );

  const setAmount = useCallback((pool, raw) => setAmounts((a) => ({ ...a, [pool]: raw === "" ? "" : formatCurrency(raw) })), []);
  const settleAmount = useCallback((pool) => setAmounts((a) => ({ ...a, [pool]: padAmount(a[pool]) })), []);

  const distribute = useCallback(async () => {
    const rs = parseMoney(amounts.rs);
    const nil = parseMoney(amounts.nil);
    if (rs <= 0 && nil <= 0) return;
    setDistributing(true);
    // Only a revenue-share budget (or a roster that never loaded) needs the
    // calculator; the allocate endpoint splits both pools once the budgets
    // are set.
    const next = rs > 0 || !scored ? await rescore(rs, added, { action: "distribute", locks: rsLocks, hidden: [...hiddenRs] }) : { roster, added };
    setDistributing(false);
    if (!next) return;
    setDistributed({ rs, nil });
  }, [amounts, scored, roster, added, hiddenRs, rsLocks, rescore]);

  const existingIds = useMemo(() => new Set([...roster, ...added].map(rowId)), [roster, added]);

  const importPlayers = useCallback(
    async (players) => {
      const fresh = players.map((p) => toImportRow(p)).filter((p) => p.sportsRadarId && !existingIds.has(p.sportsRadarId));
      if (!fresh.length) return;
      const imports = [...added, ...fresh];
      setAdded(imports);
      const next = await rescore(distributed.rs, imports, { locks: rsLocks, hidden: [...hiddenRs] });
      if (next) toast.success(`Added ${fresh.length} player${fresh.length === 1 ? "" : "s"} to the roster`);
    },
    [added, existingIds, distributed.rs, rsLocks, hiddenRs, rescore],
  );

  const removePlayer = useCallback((id) => {
    setAdded((list) => list.filter((p) => rowId(p) !== id));
    setHiddenRs((s) => (s.has(id) ? toggleIn(s, id) : s));
    setHiddenNil((s) => (s.has(id) ? toggleIn(s, id) : s));
    setRsLocks((locks) => withLock(locks, id, null));
    setNilLocks((locks) => withLock(locks, id, null));
  }, []);

  const toggleHidden = useCallback((id, pool) => (pool === "nil" ? setHiddenNil : setHiddenRs)((s) => toggleIn(s, id)), []);

  // Lock a player's amount in one pool ("rs" or "nil"), in whole cents, or
  // unlock it with null. An amount the session would not save is refused.
  const setLock = useCallback((pool, id, value) => {
    const amount = value === null || value === undefined ? null : Math.round(Number(value) * 100) / 100;
    if (amount !== null && !(amount >= 0 && amount < MAX_LOCK)) return;
    (pool === "nil" ? setNilLocks : setRsLocks)((locks) => withLock(locks, id, amount));
  }, []);

  const reload = useCallback(() => rescore(distributed.rs, added, { locks: rsLocks, hidden: [...hiddenRs] }), [rescore, distributed.rs, added, rsLocks, hiddenRs]);

  // Run the call that failed again: Distribute, or the roster load.
  const retry = useCallback(() => {
    if (error?.action === "distribute") return distribute();
    return load(distributed, added, { hiddenRs, rsLocks });
  }, [error, distribute, load, distributed, added, hiddenRs, rsLocks]);

  const merged = useMemo(() => mergeRows(roster, added), [roster, added]);

  // What the allocate endpoint splits. Until the open team's roster is
  // scored nothing is: a failed load shows "-" rather than the budgets
  // spread over the imports alone.
  const request = useMemo(
    () => (scored ? allocateRequestOf(merged, { sport, rsBudget: distributed.rs, nilBudget: distributed.nil, hiddenRs, hiddenNil, rsLocks, nilLocks }) : null),
    [scored, merged, sport, distributed, hiddenRs, hiddenNil, rsLocks, nilLocks],
  );

  useEffect(() => {
    const mine = ++allocateTicket.current;
    if (!request || (!request.revshare && !request.nil)) {
      setAllocation(NO_ALLOCATION);
      return undefined;
    }
    setAllocation((a) => (a.status === "pending" ? a : { ...a, status: "pending", error: null }));
    const controller = new AbortController();
    const current = () => mine === allocateTicket.current && !controller.signal.aborted;
    const budgets = { rs: request.revshare?.budget || 0, nil: request.nil?.budget || 0 };
    const timer = setTimeout(() => {
      allocateCacheShare(request, { signal: controller.signal }).then(
        (data) => {
          if (current()) setAllocation({ rs: data?.revshare || null, nil: data?.nil || null, budgets, status: "idle", error: null });
        },
        (err) => {
          if (current()) setAllocation((a) => ({ ...a, status: "error", error: failureOf(err) }));
        },
      );
    }, ALLOCATE_DELAY_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [request, allocateRun]);

  // Post the split again after a failure.
  const retryAllocate = useCallback(() => setAllocateRun((n) => n + 1), []);

  // The last answer's pools while they are split at the budgets now
  // distributed: after a lock or a hide the last split stays on screen until
  // the new one lands (or the post fails), but a new budget reads "-" until
  // its own split comes back.
  const split = useMemo(
    () => ({
      rs: allocation.budgets.rs === distributed.rs ? allocation.rs : null,
      nil: allocation.budgets.nil === distributed.nil ? allocation.nil : null,
    }),
    [allocation, distributed],
  );

  const rows = useMemo(
    () => mergeAllocation(applyAllocation(merged, { rsBudget: scored ? distributed.rs : 0, hiddenRs, hiddenNil }), split, { rsLocks, nilLocks }),
    [merged, scored, distributed.rs, hiddenRs, hiddenNil, split, rsLocks, nilLocks],
  );

  const pools = useMemo(() => ({ rs: poolSummary(rows, "rs", distributed.rs, split.rs), nil: poolSummary(rows, "nil", distributed.nil, split.nil) }), [rows, distributed, split]);

  const slice = useMemo(
    () => ({
      revenueAmount: padAmount(amounts.rs),
      nilAmount: padAmount(amounts.nil),
      distributedRs: amountText(distributed.rs),
      distributedNil: amountText(distributed.nil),
      budgetPurpose: budgetPurposeOf(distributed.rs, distributed.nil),
      hiddenRs: [...hiddenRs],
      hiddenNil: [...hiddenNil],
      payoutOverrides: rsLocks,
      nilLocks,
      addedPlayers: added,
    }),
    [amounts, distributed, hiddenRs, hiddenNil, rsLocks, nilLocks, added],
  );

  return {
    openedKey,
    rows,
    pools,
    amounts,
    distributed,
    allocation,
    loading,
    distributing,
    error,
    dataStatus,
    existingIds,
    canDistribute: !!team && !loading && (parseMoney(amounts.rs) > 0 || parseMoney(amounts.nil) > 0),
    slice,
    open,
    setAmount,
    settleAmount,
    distribute,
    importPlayers,
    removePlayer,
    toggleHidden,
    setLock,
    reload,
    retry,
    retryAllocate,
  };
};
